import { useMemo } from "react";

import classNames from "classnames";
import { noop } from "neetocist";
import { Tooltip, Typography } from "neetoui";
import PropTypes from "prop-types";
import { pluck } from "ramda";
import { useTranslation } from "react-i18next";

import { groupBy } from "./utils";

const EmojiReactions = ({
  isUpdating,
  reactions,
  onReactionClick = noop,
  emojiSize = "text-sm",
  emojiBoxStyle = "neeto-ui-bg-white py-1 px-2",
}) => {
  const { t } = useTranslation();
  const groupedReactions = useMemo(
    () => groupBy(reactions || [], "unified"),
    [reactions]
  );

  return (
    <>
      {Object.entries(groupedReactions).map(([unified, grouped]) => {
        const groupedNames = pluck("userName", reactions).join(", ");
        const reaction = grouped[0];

        return (
          <Tooltip
            key={unified}
            position="top"
            touch={["hold", 500]}
            content={
              <div className="flex flex-col items-center justify-center">
                <div className="neeto-ui-bg-white neeto-ui-rounded mb-1 flex h-10 w-10 items-center justify-center">
                  {reaction.native}
                </div>
                <p className="text-center text-xs">
                  {t("neetoMolecules.reactions.content", { groupedNames })}
                </p>
              </div>
            }
          >
            <div
              data-testid={`emoji-reactions-${reaction.unified}`}
              className={classNames(
                [
                  "neeto-ui-rounded-full hover:neeto-ui-bg-gray-100 neeto-ui-bg-gray-100 flex cursor-pointer items-center justify-center px-2 py-1",
                  emojiBoxStyle,
                ],
                { "opacity-80": isUpdating }
              )}
              onClick={() =>
                !isUpdating &&
                onReactionClick(
                  {
                    native: reaction.native,
                    unified: reaction.unified,
                  },
                  grouped
                )
              }
            >
              <Typography
                className="flex items-center gap-x-2 text-sm"
                style="body2"
              >
                <span className={emojiSize}>{reaction.native}</span>
                <span>{grouped.length}</span>
              </Typography>
            </div>
          </Tooltip>
        );
      })}
    </>
  );
};

EmojiReactions.propTypes = {
  /**
   * Array of reactions to be displayed
   */
  reactions: PropTypes.arrayOf(
    PropTypes.shape({
      native: PropTypes.string,
      unified: PropTypes.string,
      userName: PropTypes.string,
    })
  ),
  /**
   * Callback function to be called when user click on an existing reaction
   */
  onReactionClick: PropTypes.func,
  /**
   * Size of the emoji
   */
  emojiSize: PropTypes.string,
  /**
   * Emoji picker box style
   */
  emojiBoxStyle: PropTypes.string,
  /**
   * Use this flag to block click action while backend is updating.
   */
  isUpdating: PropTypes.bool,
};

export default EmojiReactions;
